/**
 * Per-match undo bookmarks (T144).
 *
 * Operator drops a bookmark before a risky sequence (DRS appeal, in-
 * flight dispute, before a system-data audit). Later, "Restore to
 * bookmark X" reverts every ball recorded since the bookmark was set
 * — implemented as a batch undo (T141) of `current_balls - bookmark.balls`.
 *
 * Bookmarks are LOCAL to the operator's match session. They don't
 * sync to the server (that would warrant its own model + endpoint).
 * Cap is 20 per match so localStorage doesn't grow unbounded.
 *
 * Restoration is bounded by MAX_BATCH_UNDO (25) on the server — if
 * the operator dropped a bookmark and then scored 30 balls, restore
 * would refuse with a clear message and recommend the QC rebuild
 * tool. That's an intentional product call: live undo is for typo
 * fixes, not "wipe the last over and a half".
 */
import { useCallback, useEffect, useState } from "react";


const MAX_BOOKMARKS = 20;
const STORAGE_PREFIX = "zcd_undo_bookmarks_";


function loadBookmarks(matchId) {
  if (!matchId) return [];
  try {
    const raw = localStorage.getItem(STORAGE_PREFIX + matchId);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr : [];
  } catch { return []; }
}


function saveBookmarks(matchId, bookmarks) {
  if (!matchId) return;
  try {
    localStorage.setItem(STORAGE_PREFIX + matchId, JSON.stringify(bookmarks.slice(-MAX_BOOKMARKS)));
  } catch { /* quota — give up silently */ }
}


export function useUndoBookmarks(matchId) {
  const [bookmarks, setBookmarks] = useState(() => loadBookmarks(matchId));

  useEffect(() => { setBookmarks(loadBookmarks(matchId)); }, [matchId]);
  useEffect(() => { saveBookmarks(matchId, bookmarks); }, [matchId, bookmarks]);

  const drop = useCallback(({ label, balls_recorded, last_ball_summary }) => {
    setBookmarks((prev) => [
      ...prev,
      {
        id: `bm_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
        label: (label || "").slice(0, 60) || "Bookmark",
        balls_recorded: balls_recorded || 0,
        ts: Date.now(),
        last_ball_summary: last_ball_summary || "",
      },
    ].slice(-MAX_BOOKMARKS));
  }, []);

  const remove = useCallback((id) => {
    setBookmarks((prev) => prev.filter((b) => b.id !== id));
  }, []);

  const clearAll = useCallback(() => setBookmarks([]), []);

  return { bookmarks, drop, remove, clearAll };
}
